import clsx from "clsx";
import dayjs from "dayjs";
import { useFormContext } from "react-hook-form";

import { useClearProductionCache, useProducePollingContext } from "@ryde/redux";
import {
  EMPTY_VALUE_DISPLAY,
  formatDatetime,
  IRichLogData,
  isValidDateTime,
  MAIN_DATE_FORMAT,
  MAIN_TIME_FORMAT_SEC,
  useFormats,
  useTimezone,
} from "@ryde/shared";

import {
  IRichLogsFilter,
  RICH_LOGS_PAGE_SIZE,
  RichLogFormFields,
} from "../../rich-logs-components";

interface IUseProductionLogFiltersFooterProps {
  logData: IRichLogData | undefined;
  filteredLogsCount: number | undefined;
}

export const useProductionLogFiltersFooter = ({
  logData,
  filteredLogsCount,
}: IUseProductionLogFiltersFooterProps) => {
  const { watch } = useFormContext<IRichLogsFilter>();
  const { timezone } = useTimezone();
  const { fetchLogs } = useProducePollingContext();
  const { formatDateTimeNormalized } = useFormats();
  const { handleClearProductionCacheClick } = useClearProductionCache();
  const { records, state, systemParams } = logData ?? {};
  const { startedAt, finishedAt, updatedAt, failedAt } = state ?? {};

  const currentPage = watch(RichLogFormFields.page) || 1;
  const totalPages = Math.ceil((filteredLogsCount || 0) / RICH_LOGS_PAGE_SIZE);

  const formattedStartedAt = formatDateTimeNormalized(
    startedAt,
    EMPTY_VALUE_DISPLAY,
  );

  const formattedFinishedAt = formatDateTimeNormalized(
    finishedAt,
    EMPTY_VALUE_DISPLAY,
  );
  const formattedLastLogSnapshotAt = formatDateTimeNormalized(
    updatedAt,
    EMPTY_VALUE_DISPLAY,
  );
  const formattedFailedLogSnapshotAt = formatDateTimeNormalized(
    failedAt,
    EMPTY_VALUE_DISPLAY,
  );

  const getDuration = (): string => {
    if (!startedAt || !isValidDateTime(startedAt)) {
      return EMPTY_VALUE_DISPLAY;
    }

    let endDate: string | undefined;

    if (finishedAt && isValidDateTime(finishedAt)) {
      endDate = finishedAt;
    } else if (updatedAt && isValidDateTime(updatedAt)) {
      endDate = updatedAt;
    } else {
      return EMPTY_VALUE_DISPLAY;
    }

    const startDate = dayjs(startedAt);
    const end = dayjs(endDate);
    const diff = end.diff(startDate, "second", true);

    if (diff <= 0) {
      return EMPTY_VALUE_DISPLAY;
    }

    const minutes = Math.floor(diff / 60);
    const seconds = Math.floor(diff % 60);

    if (minutes === 0) {
      return `${seconds}s`;
    }

    return `${minutes}m ${seconds}s`;
  };

  const hasValidTimes =
    isValidDateTime(startedAt) ||
    isValidDateTime(finishedAt) ||
    isValidDateTime(updatedAt);
  const duration = getDuration();

  const {
    totalBookingCount,
    bookingsAlreadyInProduceCount,
    joinedRideCount,
    joinedBookingCount,
    producedRideCount,
    producedBookingCount,
    producedAndJoinedRideIds,
    producedAndJoinedRideFirstDateISO,
    producedAndJoinedRideEndDateISO,
  } = state ?? {};

  const formattedProducedRideFirstDate = isValidDateTime(
    producedAndJoinedRideFirstDateISO,
  )
    ? formatDatetime({
        dateTimeISO: producedAndJoinedRideFirstDateISO,
        timeFormat: MAIN_TIME_FORMAT_SEC,
        dateFormat: MAIN_DATE_FORMAT,
        timezone,
      })
    : "";

  const formattedProducedRideEndDate = isValidDateTime(
    producedAndJoinedRideEndDateISO,
  )
    ? formatDatetime({
        dateTimeISO: producedAndJoinedRideEndDateISO,
        timeFormat: MAIN_TIME_FORMAT_SEC,
        dateFormat: MAIN_DATE_FORMAT,
        timezone,
      })
    : "";

  const hasProductionData = !!(
    formattedProducedRideFirstDate ||
    formattedProducedRideEndDate ||
    producedRideCount ||
    producedAndJoinedRideIds?.length
  );

  const isFailed = !!formattedFailedLogSnapshotAt;
  const isInProgress = !formattedFinishedAt && !isFailed;

  const getTimeInfoText = (): string => {
    if (isFailed) {
      return `Failed at: ${formattedFailedLogSnapshotAt}`;
    }

    if (isInProgress) {
      return `Log updated at: ${formattedLastLogSnapshotAt}`;
    }

    return `Finished at: ${formattedFinishedAt}`;
  };

  const getTimeInfoClassName = (): string => {
    return clsx("log__filters-footer-time-info", {
      [`${"log__filters-footer-time-info--failed"}`]: isFailed,
      [`${"log__filters-footer-time-info--in-progress"}`]: isInProgress,
    });
  };

  return {
    currentPage,
    totalPages,
    formattedStartedAt,
    formattedFinishedAt,
    formattedLastLogSnapshotAt,
    formattedFailedLogSnapshotAt,
    duration,
    hasValidTimes,
    formattedProducedRideFirstDate,
    formattedProducedRideEndDate,
    hasProductionData,
    timeInfoText: getTimeInfoText(),
    timeInfoClassName: getTimeInfoClassName(),
    fetchLogs,
    handleClearProductionCacheClick,
    records,
    state,
    systemParams,
    totalBookingCount,
    bookingsAlreadyInProduceCount,
    joinedRideCount,
    joinedBookingCount,
    producedRideCount,
    producedBookingCount,
  };
};
